import { CloseOutlined } from "@ant-design/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Button, Typography } from "antd";
import { Navigate } from "react-router-dom";
import { useObject } from "@/entities/object/index.ts";
import { projectQueries } from "@/entities/project/index.ts";
import {
  JournalVersionsListPanel,
  useJournalVersionsStore,
} from "@/features/journal-version-list";
import type { JournalVersion } from "@/features/journal-version-list";
import { useFlags } from "@/shared/lib/flags";
import { useCoordinator } from "@/shared/lib/hooks";
import { routes, useNavigate } from "@/shared/lib/react-router";
import { Breadcrumbs } from "@shared/ui";
import "./journal-versions-page.scss";

const { Title } = Typography;

export const JournalVersionsPage = () => {
  const isVersionsEnabled = useFlags("ojrFormationFlowEnabledV1");
  const { projectId, objectId, mainJournalId, titleId } = useCoordinator() as {
    projectId: string;
    objectId: string;
    mainJournalId: string;
    titleId: string;
  };
  const navigate = useNavigate();
  const versions = useJournalVersionsStore(
    (s: { versions: JournalVersion[] }) => s.versions,
  );
  const { data: project } = useSuspenseQuery(
    projectQueries.getProject({ projectId }),
  );
  const { data: object } = useObject({ objectId });

  if (!isVersionsEnabled) {
    return <Navigate to=".." replace />;
  }

  const handleVersionClick = (version: JournalVersion) => {
    navigate(
      routes.journals.journalVersionTitle(
        projectId,
        objectId,
        mainJournalId,
        titleId,
        version.versionId,
      ),
    );
  };

  const breadcrumbs = [
    { path: routes.projects(), label: "Проекты" },
    { path: routes.project.root({ projectId }), label: project.shortName },
    { path: routes.objects.root(projectId), label: "Объекты" },
    {
      path: routes.objects.object(projectId, objectId),
      label: object?.shortName ?? "",
    },
    {
      path: routes.objects.journals(projectId, objectId),
      label: "Журналы",
    },
    {
      path: routes.journals.versions(projectId, objectId, mainJournalId, titleId),
      label: "Версии ОЖР",
    },
  ];

  return (
    <div className="general-view-container">
      <div className="journal-versions-page">
        <div className="journal-versions-page__header">
          <Breadcrumbs routes={breadcrumbs} />
          <div className="journal-versions-page__title-row">
            <Title level={3} className="journal-versions-page__title">
              Версии ОЖР
            </Title>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => navigate(-1)}
              className="journal-versions-page__close-btn"
            />
          </div>
        </div>

        <JournalVersionsListPanel
          versions={versions}
          onVersionRowClick={handleVersionClick}
        />
      </div>
    </div>
  );
};
